/**
 * Central unit status change function.
 * ALL status changes MUST flow through this function to ensure
 * consistent ripple effects across the system.
 *
 * This uses localStorage (DB.get/DB.set) — same as the rest of the app.
 * Supabase sync happens via the existing background push mechanism.
 */
import { DB } from '../data/database';

import { writeAuditLog } from './audit';

const DB_KEYS = { vehicle: 'vehicles', engine: 'engines', part: 'spareParts' };

/**
 * @param {string} unitId
 * @param {'vehicle'|'engine'|'part'} unitType
 * @param {string} newStatus
 * @param {object} options
 * @param {string} [options.assignedTo]    - customer ID when selling/assigning
 * @param {number} [options.fobPrice]      - agreed FOB price
 * @param {string} [options.containerId]   - container ID when loading/shipping
 * @param {string} [options.updatedBy]     - name of user making the change
 * @param {string} [options.country]       - destination country (for delivered)
 * @param {string} [options.city]          - destination city
 * @param {object} [options.extraFields]   - any additional fields to set on the unit
 * @returns {{ success: boolean, oldStatus: string, newStatus: string }}
 */
export default function updateUnitStatus(unitId, unitType, newStatus, options = {}) {
  const {
    assignedTo = null,
    fobPrice = null,
    containerId = null,
    updatedBy = null,
    country = null,
    city = null,
    extraFields = {},
  } = options;

  const key = DB_KEYS[unitType];
  if (!key) { console.error('updateUnitStatus: invalid unitType', unitType); return { error: 'Invalid unit type' }; }

  const list = DB.get(key) || [];
  const unitIndex = list.findIndex(u => u.id === unitId);
  if (unitIndex === -1) { console.error('updateUnitStatus: unit not found', unitId); return { error: 'Unit not found' }; }

  const unit = list[unitIndex];
  const oldStatus = unit.status;

  // ── Build the update object ──
  const update = { status: newStatus, ...extraFields };
  if (assignedTo) update.assignedTo = assignedTo;
  if (country) update.country = country;
  if (city) update.city = city;
  if (fobPrice && newStatus === 'sold') {
    update.pricing = { ...(unit.pricing || {}), fob_price_usd: fobPrice };
  }

  // ── RIPPLE EFFECTS ──

  // SOLD or SOLD_AWAITING_PAYMENT
  if (newStatus === 'sold_awaiting_payment' || newStatus === 'sold') {
    // Remove from OTHER customers' stock (not the buyer)
    if (assignedTo) {
      const stock = DB.get('customerStock') || [];
      const toRemove = stock.filter(s => s.unitId === unitId && s.customerId !== assignedTo);
      if (toRemove.length > 0) {
        DB.set('customerStock', stock.filter(s => !(s.unitId === unitId && s.customerId !== assignedTo)));
        writeAuditLog({
          action: 'customer_stock.removed_by_system',
          entityType: 'customer_stock',
          entityId: unitId,
          entityLabel: `Unit ${unitId} sold — removed from ${toRemove.length} other customers`,
          details: { unit_id: unitId, new_status: newStatus, buyer: assignedTo, removed_from: toRemove.map(s => s.customerId), trigger: 'sold_ripple', updated_by: updatedBy },
        });
      }
    }
    // Mark all active yard bids as 'lost'
    const bids = DB.get('yardBids') || [];
    const updatedBids = bids.map(b =>
      b.unit_id === unitId && b.status === 'active' ? { ...b, status: 'lost' } : b
    );
    if (JSON.stringify(bids) !== JSON.stringify(updatedBids)) DB.setAsync('yardBids', updatedBids);

    // Update website status to 'sold'
    update.website_status = 'sold';
    if (!unit.sold_at) update.sold_at = new Date().toISOString();

    // Clear any active hold
    update.hold_for_customer_id = null;
    update.hold_until = null;
    update.hold_approved_by = null;
    update.hold_reason = null;
    update.hold_requested_by = null;
  }

  // AVAILABLE (reverting)
  if (newStatus === 'available') {
    update.assignedTo = null;
    if (unit.show_on_website) {
      update.website_status = 'live';
    }
  }

  // CUTTING / DISMANTLED — remove from website only, keep customer stock (wishlist)
  if (newStatus === 'cutting' || newStatus === 'dismantled') {
    update.show_on_website = false;
    update.website_status = 'hidden';
    update.is_featured = false;
    // NOTE: Do NOT remove from customer stock — it's a wishlist.
    // Only the customer themselves can remove items from their stock.
    // Only sold/sold_awaiting_payment removes from OTHER customers.
  }

  // CONVERTED — hide from website
  if (newStatus === 'converted') {
    update.show_on_website = false;
    update.website_status = 'hidden';
    update.is_featured = false;
  }

  // DELIVERED
  if (newStatus === 'delivered') {
    // If container provided, check if all units in container are delivered
    if (containerId) {
      // This is informational — container status is managed separately
    }
  }

  // IN_TRANSIT (from container seal)
  if (newStatus === 'in_transit') {
    if (containerId) update.containerId = containerId;
    update.website_status = 'sold';
    if (!unit.loaded_at) update.loaded_at = new Date().toISOString();
  }

  // LOADING
  if (newStatus === 'loading') {
    if (containerId) update.containerId = containerId;
    update.website_status = 'sold';
    if (!unit.loaded_at) update.loaded_at = new Date().toISOString();
  }

  // ── Apply the update ──
  const updatedUnit = { ...unit, ...update };
  list[unitIndex] = updatedUnit;
  DB.set(key, list);

  console.log(`[StatusChange] ${unit.sku || unitId}: ${oldStatus} → ${newStatus}${updatedBy ? ` by ${updatedBy}` : ''}`);

  return { success: true, oldStatus, newStatus, unit: updatedUnit };
}

/**
 * Helper: get the DB key for a unit type string
 */
export function getUnitDbKey(unitType) {
  return DB_KEYS[unitType] || null;
}

/**
 * Helper: detect unit type from the tab or item
 */
export function detectUnitType(tabOrItem) {
  if (typeof tabOrItem === 'string') {
    if (tabOrItem === 'vehicles') return 'vehicle';
    if (tabOrItem === 'engines') return 'engine';
    if (tabOrItem === 'parts' || tabOrItem === 'spareParts') return 'part';
  }
  if (tabOrItem?._type) return tabOrItem._type;
  if (tabOrItem?.make && !tabOrItem?.partName) return 'vehicle';
  if (tabOrItem?.engineName) return 'engine';
  return 'part';
}
